import type { CurrentUser } from '@/shared/types/auth';

export const WORKSPACE_ACCESS_PROFILE = 'workspace.access.configured';

export const WORKSPACE_ACCESS_CATALOG = [
  { key: 'sales', label: 'Sales CRM', description: 'Leads, deals and contacts', group: 'crm' },
  { key: 'sales_documents', label: 'Sales Documents', description: 'Quotations, invoices and sales files', group: 'crm' },
  { key: 'scraper', label: 'Lead Scraper', description: 'Lead generation and scraping jobs', group: 'crm' },
  { key: 'call_center', label: 'Call Center', description: 'Calling workspace and call records', group: 'crm' },
  { key: 'accounts', label: 'Accounts', description: 'Finance, banking, expenses and reports', group: 'finance' },
  { key: 'catalog', label: 'Catalog', description: 'Products and services', group: 'operations' },
  { key: 'inventory', label: 'Inventory', description: 'Stock and inventory control', group: 'operations' },
  { key: 'procurement', label: 'Procurement', description: 'Purchasing and suppliers', group: 'operations' },
  { key: 'sales_operations', label: 'Sales Operations', description: 'Operational sales workflows', group: 'operations' },
  { key: 'pos', label: 'Point of Sale', description: 'POS billing and checkout', group: 'operations' },
  { key: 'manufacturing', label: 'Manufacturing', description: 'Production orders, BOMs, batch operations and quality', group: 'operations' },
  // The Dispatch team's own module. Must stay in sync with WORKSPACE_MODULES in
  // src/workspace-access.js — a key on one side only is silently stripped the
  // moment an access profile is explicitly saved.
  { key: 'dispatch', label: 'Dispatch', description: 'Confirmed orders, stock check, delivery challans and stock-facing masters', group: 'operations' },
  // ── Services & Projects ────────────────────────────────────────────────────
  // Deliberately granular rather than one blanket key: these screens carry very
  // different sensitivity. Pricing and margin data (rate cards, profitability)
  // should not ride along with day-to-day delivery work (projects, timesheets).
  { key: 'rate_cards', label: 'Rate Cards', description: 'Service pricing and billing rates', group: 'services' },
  { key: 'proposals', label: 'Proposals & SOWs', description: 'Statements of work and client proposals', group: 'services' },
  { key: 'contracts', label: 'Contracts', description: 'Signed client contracts and renewals', group: 'services' },
  { key: 'services_projects', label: 'Projects & Campaigns', description: 'Delivery projects and marketing campaigns', group: 'services' },
  { key: 'timesheets', label: 'Timesheets', description: 'Time tracking against projects', group: 'services' },
  { key: 'billing', label: 'Billing', description: 'Project billing runs and invoicing', group: 'services' },
  { key: 'revenue_recognition', label: 'Revenue Recognition', description: 'Deferred and recognised revenue schedules', group: 'services' },
  { key: 'profitability', label: 'Profitability', description: 'Project margin and profitability analysis', group: 'services' },
  { key: 'work', label: 'IT / Work Management', description: 'Tasks, projects, demos, meetings and notes', group: 'work' },
  { key: 'service_desk', label: 'Service Desk', description: 'Internal and client support tickets', group: 'work' },
  { key: 'it_operations', label: 'IT Operations', description: 'Assets, monitoring incidents and infrastructure support', group: 'work' },
  { key: 'hr', label: 'HR & People', description: 'Team, attendance, leave and organization', group: 'hr' },
  { key: 'payroll', label: 'Payroll', description: 'Payroll and ledger', group: 'hr' },
  { key: 'communication', label: 'Communication', description: 'Announcements and activity', group: 'system' },
  { key: 'ai', label: 'AI Workspace', description: 'AI tools and copilot', group: 'system' },
  { key: 'analytics', label: 'Analytics & Reports', description: 'Company insights and reporting', group: 'system' },
] as const;

export type WorkspaceAccessKey = (typeof WORKSPACE_ACCESS_CATALOG)[number]['key'];
export type WorkspaceAccessGroup = (typeof WORKSPACE_ACCESS_CATALOG)[number]['group'];

export const ALL_WORKSPACE_ACCESS = WORKSPACE_ACCESS_CATALOG.map(({ key }) => key);

export const DEPARTMENT_WORKSPACE_DEFAULTS: Record<string, WorkspaceAccessKey[]> = {
  sales: ['sales', 'sales_documents', 'scraper', 'call_center', 'catalog', 'sales_operations', 'pos', 'work', 'service_desk', 'communication', 'ai'],
  hr: ['work', 'service_desk', 'hr', 'payroll', 'communication'],
  // 'sales' grants Accounts the Leads and Deals screens. What they can see there
  // is narrowed server-side to closed_won deals only, in any department including
  // their own — the module opens the screen, the API decides the rows.
  // catalog/inventory/procurement/pos are deliberately NOT here — they are an
  // admin-only addition (DEPARTMENT_ROLE_WORKSPACE_ADDITIONS.accounts.admin
  // below). Listing them here too previously granted every accounts EMPLOYEE
  // that operational access, not just admins.
  // 'dispatch' mirrors the backend entry (src/workspace-access.js) — Accounts
  // has Pending Dispatch access (client request #104). 'manufacturing' is
  // granted server-side too but deliberately left out of the SIDEBAR here:
  // ten production screens are not Accounts' daily work at RBM.
  accounts: ['sales', 'sales_documents', 'accounts', 'sales_operations', 'dispatch', 'payroll', 'work', 'service_desk', 'communication', 'billing', 'revenue_recognition', 'profitability', 'hr'],
  // production / operations / general_manager existed only in the backend
  // table, so a user in one of these departments got working dispatch APIs
  // but a sidebar with no Pending Dispatch or Dispatch & Challans at all.
  production: ['manufacturing', 'inventory', 'procurement', 'catalog', 'work', 'service_desk', 'communication', 'dispatch', 'sales_operations'],
  operations: ['sales', 'sales_documents', 'sales_operations', 'manufacturing', 'inventory', 'procurement', 'work', 'service_desk', 'communication', 'accounts', 'analytics', 'dispatch'],
  general_manager: ['sales', 'sales_documents', 'scraper', 'call_center', 'accounts', 'catalog', 'inventory', 'procurement', 'sales_operations', 'pos', 'manufacturing', 'work', 'service_desk', 'hr', 'payroll', 'communication', 'analytics', 'dispatch'],
  // Mirrors src/workspace-access.js's 'dispatch' department entry, which the
  // frontend table was missing entirely.
  // No bare 'sales' — same as the backend entry, which leaves it out on
  // purpose ("none of which is Dispatch's job"). With it here the Dispatch
  // team's sidebar listed the whole Sales CRM, every screen of which the
  // server then refused. Sampling stays reachable through its 'dispatch' gate.
  dispatch: ['sales_operations', 'inventory', 'dispatch', 'work', 'service_desk', 'communication'],
  // Mirrors src/workspace-access.js's 'field_executive' department entry —
  // missing here, so every field screen (Visits, Contacts, Leads, Sampling)
  // sent a Field Executive back to the dashboard (2026-10-07).
  field_executive: ['sales', 'sales_documents', 'call_center', 'work', 'service_desk', 'communication', 'ai'],
  it: ['work', 'service_desk', 'it_operations', 'communication', 'ai', 'services_projects', 'timesheets'],
  support: ['work', 'service_desk', 'communication', 'ai'],
  customer_support: ['work', 'service_desk', 'communication', 'ai'],
  digital_marketing: ['work', 'service_desk', 'communication', 'ai', 'services_projects', 'timesheets'],
  // Management is the only department that sees the full Services & Projects
  // surface, margin included.
  management: ['sales', 'sales_documents', 'accounts', 'catalog', 'inventory', 'procurement', 'sales_operations', 'pos', 'work', 'service_desk', 'hr', 'payroll', 'communication', 'analytics', 'rate_cards', 'proposals', 'contracts', 'services_projects', 'timesheets', 'billing', 'revenue_recognition', 'profitability', 'dispatch'],
};

/**
 * Additive modules required by a role inside a department. These are kept
 * intentionally narrow: selecting a role must never silently grant unrelated
 * company-wide workspaces.
 */
export const DEPARTMENT_ROLE_WORKSPACE_ADDITIONS: Record<string, Partial<Record<string, WorkspaceAccessKey[]>>> = {
  sales: {
    manager: ['sales_operations'],
    admin: ['sales_operations', 'analytics'],
    sales_coordinator: ['sales_operations'],
  },
  hr: {
    manager: ['analytics'],
    admin: ['analytics'],
  },
  it: {
    manager: ['analytics'],
    admin: ['analytics'],
  },
  digital_marketing: {
    manager: ['analytics'],
    admin: ['analytics'],
  },
  accounts: {
    manager: ['analytics'],
    admin: ['catalog', 'inventory', 'procurement', 'pos', 'analytics'],
  },
};

export const FIELD_EXECUTIVE_WORKSPACE: readonly WorkspaceAccessKey[] = Object.freeze(['sales', 'sales_documents', 'call_center', 'work', 'service_desk', 'communication', 'ai']);

export function permissionsForWorkspaceAccess(access: readonly WorkspaceAccessKey[]): string[] {
  return [WORKSPACE_ACCESS_PROFILE, ...access.map((key) => `workspace.${key}`)];
}

export function defaultWorkspaceAccess(department: string): WorkspaceAccessKey[] {
  return [...(DEPARTMENT_WORKSPACE_DEFAULTS[department] ?? ['work', 'service_desk', 'communication'])];
}

/** Defaults that are mandatory for the selected department + role. */
export function defaultWorkspaceAccessForRole(department: string, role: string): WorkspaceAccessKey[] {
  return [...new Set([
    ...defaultWorkspaceAccess(department),
    ...(DEPARTMENT_ROLE_WORKSPACE_ADDITIONS[department]?.[role] ?? []),
  ])];
}

export function isRohitSharmaUser(
  user?: { username?: string | null; display_name?: string | null; displayName?: string | null; role?: string | null; name?: string | null } | null,
): boolean {
  if (!user) return false;
  const uname = String(user.username || '').trim().toLowerCase();
  const dname = String(user.display_name || user.displayName || user.name || '').trim().toLowerCase();

  // Mirrors src/shared/users/rohit-sharma-policy.js. The prefix form
  // (`uname.startsWith('asm')`) was removed on both sides: the client's rule is
  // that ROHIT SHARMA sees only his own assigned contacts, and a prefix would
  // have handed the same restriction to every future ASM account. asm1/asm2 are
  // named explicitly, which is exactly who exists today.
  if (uname === 'asm1' || uname === 'asm2') return true;
  if (dname.includes('rohit sharma') || uname.includes('rohit.sharma') || uname === 'rohit') return true;
  return false;
}

export function effectiveWorkspaceAccess(user?: CurrentUser | null): WorkspaceAccessKey[] {
  if (!user) return [];
  if (user.role === 'superadmin') return [...ALL_WORKSPACE_ACCESS];
  // A driver account is department 'dispatch', which — like every other
  // department here — grants a full CRM workspace (sales, inventory,
  // sales_operations...) via whatever permissions were persisted at account
  // creation (src/workspace-access.js on the backend does list a broad set
  // for 'dispatch'). A driver's only screen is the Driver Portal
  // (frontend/src/routes/paths.ts's DRIVER_NAV_GROUPS) — this overrides
  // whatever was granted rather than unioning with it, so a stale/mis-seeded
  // permissions array can never widen a driver's access back out.
  if (user.role === 'driver') return [];
  // Rohit Sharma (asm1 / Area Sales Manager): grants workspace keys for the 4
  // sections client assigned (Sales CRM, Field Sales, Workspace, Communication & Oversight).
  if (isRohitSharmaUser(user)) {
    const permissions = user.permissions ?? [];
    const selected = permissions
      .filter((permission) => permission.startsWith('workspace.') && permission !== WORKSPACE_ACCESS_PROFILE && !permission.startsWith('workspace.section.'))
      .map((permission) => permission.slice('workspace.'.length))
      .filter((key): key is WorkspaceAccessKey => ALL_WORKSPACE_ACCESS.includes(key as WorkspaceAccessKey));
    return [...new Set<WorkspaceAccessKey>([
      'sales',
      'sales_documents',
      'sales_operations',
      'work',
      'service_desk',
      'communication',
      'ai',
      ...selected,
    ])];
  }
  // A Field Executive's screens are fixed (FIELD_NAV_GROUPS in routes/paths.ts)
  // whatever department they sit in; the server still checks every API.
  if (user.role === 'field_executive') return [...FIELD_EXECUTIVE_WORKSPACE];

  const permissions = user.permissions ?? [];
  const selected = permissions
    .filter((permission) => permission.startsWith('workspace.') && permission !== WORKSPACE_ACCESS_PROFILE && !permission.startsWith('workspace.section.'))
    .map((permission) => permission.slice('workspace.'.length))
    .filter((key): key is WorkspaceAccessKey => ALL_WORKSPACE_ACCESS.includes(key as WorkspaceAccessKey));

  // A configured profile is authoritative, including removals — mirrors
  // grantedSections()'s marker check in moduleSections.ts and the backend's
  // equivalent in src/workspace-access.js. Before this fix `selected` was
  // always unioned with roleDefaults below, so unchecking a department
  // default in UserAccessModal never actually took it away.
  if (permissions.includes(WORKSPACE_ACCESS_PROFILE)) return [...new Set(selected)];

  const roleDefaults = defaultWorkspaceAccessForRole(user.department || 'sales', user.role || 'employee');
  return [...new Set([...roleDefaults, ...selected])];
}

export function hasWorkspaceAccess(user: CurrentUser | null | undefined, access: WorkspaceAccessKey): boolean {
  return user?.role === 'superadmin' || effectiveWorkspaceAccess(user).includes(access);
}
